import { FC, useState, useMemo } from 'react';
import { Box, IconButton, makeStyles, styled } from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import moment, { Moment } from 'moment';

const QUICK_EXTEND_OPTIONS = [7, 10, 14, 30];
const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    gap: 16,
    padding: '16px 0'
  },
  quickOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 16,
    minWidth: 130,
    backgroundColor: '#fff',
    borderRadius: 16
  },
  quickOption: {
    padding: '8px 12px',
    borderRadius: 22,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F0F0F0'
    }
  },
  quickOptionText: {
    color: '#5D7083',
    fontWeight: 600,
    fontSize: 12
  },
  quickOptionSelected: {
    backgroundColor: '#E3F2FD'
  },
  quickOptionTextSelected: {
    color: '#1E96FC'
  },
  calendarContainer: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16
  },
  monthYearText: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer'
  },
  navigationButtons: {
    display: 'flex',
    gap: 4
  },
  weekdaysRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4,
    marginBottom: 8
  },
  weekday: {
    textAlign: 'center',
    padding: '4px 0',
    fontSize: 11,
    fontWeight: 500,
    color: '#9E9E9E'
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4
  },
  dayCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: 14,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#E3F2FD'
    }
  },
  dayCellSelected: {
    backgroundColor: '#1E96FC',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1E96FC'
    }
  },
  dayCellDisabled: {
    color: '#BDBDBD',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  dayCellOutsideMonth: {
    color: '#BDBDBD'
  },
  dayCellToday: {
    border: '1px solid #1E96FC'
  }
}));

const NavButton = styled(IconButton)({
  padding: 4,
  '& svg': {
    fontSize: 20
  }
});

interface DatePickerProps {
  selectedDate: Moment;
  onDateChange: (date: Moment) => void;
}

export const DatePicker: FC<DatePickerProps> = ({
  selectedDate,
  onDateChange
}) => {
  const classes = useStyles();
  const [currentMonth, setCurrentMonth] = useState(moment(selectedDate));
  const today = useMemo(() => moment().startOf('day'), []);

  const handleQuickExtend = (days: number) => {
    const newDate = moment().add(days, 'days').startOf('day');
    onDateChange(newDate);
    setCurrentMonth(moment(newDate));
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => moment(prev).subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => moment(prev).add(1, 'month'));
  };

  const calendarDays = useMemo(() => {
    const startOfMonth = moment(currentMonth).startOf('month');
    const endOfMonth = moment(currentMonth).endOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = endOfMonth.date();

    const days: { date: Moment; isCurrentMonth: boolean }[] = [];

    // Previous month days
    for (let i = startDay - 1; i >= 0; i -= 1) {
      days.push({
        date: moment(startOfMonth).subtract(i + 1, 'days'),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i += 1) {
      days.push({
        date: moment(currentMonth).date(i),
        isCurrentMonth: true
      });
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i += 1) {
      days.push({
        date: moment(endOfMonth).add(i, 'days'),
        isCurrentMonth: false
      });
    }

    return days;
  }, [currentMonth]);

  const isDateDisabled = (date: Moment) => date.isBefore(today, 'day');

  const isDateSelected = (date: Moment) => date.isSame(selectedDate, 'day');

  const isToday = (date: Moment) => date.isSame(today, 'day');

  const handleDayClick = (date: Moment) => {
    if (!isDateDisabled(date)) {
      onDateChange(date);
    }
  };

  const getSelectedQuickOption = () => {
    const diffDays = selectedDate.diff(today, 'days');
    return QUICK_EXTEND_OPTIONS.includes(diffDays) ? diffDays : null;
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.quickOptions}>
        {QUICK_EXTEND_OPTIONS.map(days => (
          <Box
            key={days}
            className={`${classes.quickOption} ${
              getSelectedQuickOption() === days
                ? classes.quickOptionSelected
                : ''
            }`}
            onClick={() => handleQuickExtend(days)}>
            <SText
              size="xs"
              fontColor={
                getSelectedQuickOption() === days ? '#1E96FC' : '#5D7083'
              }
              weight="semibold">
              Extend for {days} days
            </SText>
          </Box>
        ))}
      </Box>

      <Box className={classes.calendarContainer}>
        <Box className={classes.calendarHeader}>
          <Box className={classes.monthYearText}>
            <SText size="md" weight="semibold">
              {currentMonth.format('MMM YYYY')}
            </SText>
          </Box>
          <Box className={classes.navigationButtons}>
            <NavButton onClick={handlePrevMonth}>
              <ChevronLeftIcon />
            </NavButton>
            <NavButton onClick={handleNextMonth}>
              <ChevronRightIcon />
            </NavButton>
          </Box>
        </Box>

        <Box className={classes.weekdaysRow}>
          {WEEKDAYS.map(day => (
            <Box key={day} className={classes.weekday}>
              {day}
            </Box>
          ))}
        </Box>

        <Box className={classes.daysGrid}>
          {calendarDays.map(({ date, isCurrentMonth }) => {
            const disabled = isDateDisabled(date);
            const selected = isDateSelected(date);
            const todayDate = isToday(date);

            return (
              <Box
                key={date?.toString()}
                className={`
                  ${classes.dayCell}
                  ${selected ? classes.dayCellSelected : ''}
                  ${disabled ? classes.dayCellDisabled : ''}
                  ${!isCurrentMonth ? classes.dayCellOutsideMonth : ''}
                  ${todayDate && !selected ? classes.dayCellToday : ''}
                `}
                onClick={() => handleDayClick(date)}>
                {date.date()}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
